import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { isEmpty, startCase } from 'lodash'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { useHistory, useParams } from 'react-router-dom'
import { IconAlertTriangle, IconLockFilled, IconLockOpen } from '@tabler/icons-react'
import { v4 as uuid } from 'uuid'
import format from 'date-fns/format'

import VariantIcon from '@/assets/images/variants.svg'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { ModalActions } from '@/components/modals/ModalActions'
import { Pill } from '@/components/pills/Pill'
import { toast } from '@/components/toasts/toast.helpers'
import { fetchPlaybookSectionWarnings } from '@/reducers/playbooks/playbook.actions'
import { openModal, closeModal } from '@/reducers/ui/ui.redux'
import { lockEntryToggle, winEntryToggle, deleteEntry } from '@/reducers/playbooks/playbook.redux'
import { Button } from '@/components/buttons/Button'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

import { PlaybookEntryActions } from './PlaybookEntryActions'
import { getReadableWarning } from '../playbookWarning.helpers'

export const PlaybookEntryComponent = ({
  accessor,
  categoryId,
  entryId,
  entry,
  readOnly,
  label,
  triggerTypes,
  isLockable,
  isWinnable,
  isRetiredNotificationType,
  checklistVariationCount,
  notClickable,
  metadata,
}) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { currentlyOpenModalId } = useSelector((state) => state.ui)
  const { warnings, validationError } = useSelector((state) => state.playbook)
  const { id, section } = useParams()
  const deleteModalId = `playbooks/delete/${section}/${entryId}`
  const valueToSend = categoryId ? { categoryId, entryId } : entryId // for nested entries
  const entryWarnings = warnings[accessor]?.[entryId]
  const hasValidationError =
    validationError?.accessor === accessor && validationError?.entry === entryId

  const handleOpenDeleteModal = () => dispatch(openModal(deleteModalId))
  const handleCloseModal = () => dispatch(closeModal())
  const openEntry = (event) => {
    if (notClickable) {
      return
    }
    event.stopPropagation()

    history.push(`/playbooks/${id}/checklist/${entryId}?tab=variations`)
  }
  const handleEdit = () => {
    if (notClickable) {
      return
    }
    const appendCategory = categoryId ? `?category_id=${categoryId}` : ''
    history.push(`/playbooks/${id}/${section}/${entryId}${appendCategory}`)
  }

  const handleDelete = (value) => {
    dispatch(deleteEntry(accessor)(value))

    toast.success(`${startCase(label)} has been deleted`)
    dispatch(fetchPlaybookSectionWarnings(accessor))
  }

  const handleWin = (value) => {
    dispatch(winEntryToggle(accessor)(value))
  }

  const handleLock = (value, is_locked, name) => {
    dispatch(lockEntryToggle(accessor)(value))

    const setToLocked = !is_locked

    toast.success(`Checklist item "${name}" has been ${setToLocked ? 'locked' : 'unlocked'}`)
  }

  const renderLockButton = () => {
    return (
      <Button
        basic
        small
        square
        data-testid="lock-button"
        disabled={readOnly || !isEmpty(entry.ai_checklist_prompt)}
        onClick={(event) => {
          event.stopPropagation()
          handleLock(valueToSend, entry.is_locked, entry.name)
        }}
        tooltip={
          entry.is_locked
            ? 'This item cannot be edited by agents'
            : 'This item can be edited by agents'
        }
        disabledTooltip="Lock is managed by AI Checklist"
      >
        {entry.is_locked ? <IconLockFilled /> : <IconLockOpen />}
      </Button>
    )
  }

  return (
    <>
      <div className="playbook-warnings-container">
        <div
          key={entryId}
          className={classNames('playbook-entry hidden-button-wrapper', {
            'playbook-entry__grid': isLockable || entry.win,
            'read-only': readOnly,
            retired: isRetiredNotificationType,
            'not-clickable': notClickable,
          })}
          onClick={handleEdit}
        >
          {isLockable && (
            <div className="playbook-entry__lock">
              {isWinnable && entry.win ? (
                <div className="flex-align-center small-gap">
                  {renderLockButton()}
                  <Pill small success data-testid="win-badge">
                    Win
                  </Pill>
                </div>
              ) : (
                renderLockButton()
              )}
            </div>
          )}

          {!isLockable && isWinnable && entry.win && (
            <Pill small success data-testid="win-badge">
              Win
            </Pill>
          )}

          <div className="playbook-entry__name">
            {entry.trigger && triggerTypes && (
              <div
                className="playbook-entry__trigger-type"
                data-testid="playbook-entry-trigger-type"
              >
                {triggerTypes.find((trigger) => trigger.value === entry.trigger?.type)?.label || ''}
              </div>
            )}
            <div className="playbook-entry-name" data-testid="playbook-entry-name">
              <span>{entry.name}</span>
              {checklistVariationCount && !readOnly && (
                <Pill
                  brand
                  content={checklistVariationCount}
                  icon={<img src={VariantIcon} alt="Variant" />}
                  style={{ marginLeft: '1rem' }}
                  onClick={openEntry}
                />
              )}
            </div>
            {metadata && (
              <div
                data-testid={`playbook-entry-metadata-container-${entryId}`}
                className="playbook-entry-metadata-container"
              >
                <span data-testid={`playbook-entry-metadata-container-${entryId}-author`}>
                  By: {metadata.createdBy}
                </span>
                <div className="playbook-entry-metadata-dot" />
                <span data-testid={`playbook-entry-metadata-container-${entryId}-created-at`}>
                  Created at: {format(new Date(metadata.createdAt), "MMM d',' yyyy h:mm aa")}
                </span>
              </div>
            )}
          </div>
          {!notClickable && (
            <PlaybookEntryActions
              isWinnable={isWinnable}
              isRetiredNotificationType={isRetiredNotificationType}
              readOnly={readOnly}
              handleEdit={handleEdit}
              handleWin={() => handleWin(valueToSend)}
              handleDelete={handleOpenDeleteModal}
              entry={entry}
            />
          )}
        </div>
        <div>
          {!readOnly && (entryWarnings?.warnings || entryWarnings?.decklist_warnings) && (
            <InteractiveTooltip
              content={
                <ul className="playbook-warnings-popup">
                  {entryWarnings?.warnings &&
                    entryWarnings.warnings.map((warning) => (
                      <li key={uuid()}>{getReadableWarning(warning)}</li>
                    ))}
                  {entryWarnings?.decklist_warnings &&
                    // eslint-disable-next-line no-unused-vars
                    Object.entries(entryWarnings.decklist_warnings).map(([dlUuid, warnings]) => {
                      return warnings.map((warning) => (
                        <li key={uuid()}>{getReadableWarning(warning)}</li>
                      ))
                    })}
                </ul>
              }
              theme="light"
            >
              <IconAlertTriangle
                className="icon-svg-large status-warning pointer"
                style={{ marginLeft: '0.5rem' }}
              />
            </InteractiveTooltip>
          )}
          {hasValidationError && (
            <InteractiveTooltip
              content={<div>{validationError?.errorMessage || 'This item requires attention'}</div>}
              theme="light"
            >
              <IconAlertTriangle
                className="icon-svg-large status-critical pointer"
                style={{ marginLeft: '0.5rem' }}
              />
            </InteractiveTooltip>
          )}
        </div>
      </div>

      {currentlyOpenModalId === deleteModalId && (
        <BasicModal
          data-testid={`${deleteModalId}-modal`}
          title={`Delete ${startCase(label)}`}
          onClose={handleCloseModal}
          show={currentlyOpenModalId === deleteModalId}
          size="tiny"
        >
          <p>
            {`Are you sure you want to delete this ${label}? ${
              label === 'checklist item'
                ? 'This means all checklist variations for this item will be removed from the agent app.'
                : ''
            }`}
          </p>
          <ModalActions
            onClose={handleCloseModal}
            onSubmit={() => handleDelete(valueToSend)}
            isDelete
          />
        </BasicModal>
      )}
    </>
  )
}

export const PlaybookEntry = withLDConsumer()(PlaybookEntryComponent)
